import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collectVideoEvidence, isVideoFile } from 'utils/videoGrading';

const VideoReviewContext = createContext(null);

export const useVideoReview = () => useContext(VideoReviewContext);

export const VideoReviewProvider = ({
    children,
    submission,
    assignment,
    selectedFileId,
    setSelectedFileId,
    selectedCriterionId,
    onSelectCriterion,
}) => {
    const [request, setRequest] = useState(null);
    const sequence = useRef(0);
    const file = submission?.files?.find(({ id }) => id === selectedFileId);
    const criteria = assignment?.rubric?.criteria;
    const evidence = useMemo(
        () =>
            collectVideoEvidence({
                grading: submission?.ai?.grading,
                criteria,
                file,
                allowUnscoped: submission?.files?.length === 1,
            }),
        [criteria, file, submission?.ai?.grading, submission?.files?.length]
    );

    const seekToEvidence = useCallback(
        (item) => {
            const target = submission?.files?.find(({ id }) => id === item.fileId);
            if (
                !isVideoFile(target) ||
                !Number.isFinite(item.start) ||
                item.start < 0 ||
                !Number.isFinite(item.end) ||
                item.end <= item.start
            )
                return;
            setSelectedFileId(target.id);
            if (item.criterionId && criteria?.some(({ id }) => id === item.criterionId))
                onSelectCriterion?.(item.criterionId);
            sequence.current += 1;
            setRequest({
                id: sequence.current,
                submissionId: submission.id,
                fileId: target.id,
                start: item.start,
                end: item.end,
            });
        },
        [criteria, onSelectCriterion, setSelectedFileId, submission]
    );

    const seekRequest = request?.submissionId === submission?.id && request?.fileId === selectedFileId ? request : null;
    useEffect(() => {
        if (request && (request.submissionId !== submission?.id || request.fileId !== selectedFileId)) setRequest(null);
    }, [request, selectedFileId, submission?.id]);

    const value = useMemo(
        () => ({
            file,
            evidence,
            criteria: criteria || [],
            selectedCriterionId,
            seekRequest,
            seekToEvidence,
        }),
        [criteria, evidence, file, seekRequest, seekToEvidence, selectedCriterionId]
    );

    return <VideoReviewContext.Provider value={value}>{children}</VideoReviewContext.Provider>;
};
